/**
 * Collection banner subcategory tabs.
 * The active tab is rendered server-side; this syncs the overflow thumb and adds
 * mouse/pen drag-to-scroll so narrow desktop viewports can reach off-screen
 * tabs (AAS-5386). Touch keeps native overflow momentum.
 */
(() => {
  'use strict';

  const DRAG_THRESHOLD = 4;
  const ROOT_SELECTOR = '.collection-banner__categories';

  const stripTrailingSlash = (url) => (url || '').replace(/\/$/, '');

  class CollectionBannerCategories {
    constructor(root) {
      this.scroller = root.querySelector('.collection-banner__categories-scroll');
      this.tabs = [...root.querySelectorAll('[data-subcategory-tab]')];
      this.thumb = root.querySelector('[data-scroll-thumb]');
      this.track = root.querySelector('[data-scroll-track]');
      this.inner = root.querySelector('.collection-banner__scroll-track-inner');

      this.dragStartX = 0;
      this.dragStartScroll = 0;
      this.isDragging = false;
      this.suppressClick = false;

      this.minThumb = 40;
      this.minThumbReady = false;
      this.thumbRaf = 0;

      this.scheduleThumbHandler = () => this.scheduleThumb();
      this.pointerDownHandler = (event) => this.handlePointerDown(event);
      this.pointerMoveHandler = (event) => this.handlePointerMove(event);
      this.pointerUpHandler = () => this.handlePointerUp();
      this.dragStartHandler = (event) => event.preventDefault();
      this.clickHandler = (event) => this.handleClick(event);
      this.keydownHandler = (event) => this.handleKeydown(event);

      if (!this.tabs.length) return;

      this.init();
    }

    init() {
      this.syncActiveTab();

      if (!this.scroller) return;

      this.scroller.addEventListener('scroll', this.scheduleThumbHandler, { passive: true });
      this.scroller.addEventListener('pointerdown', this.pointerDownHandler);
      this.scroller.addEventListener('dragstart', this.dragStartHandler);
      this.scroller.addEventListener('click', this.clickHandler, true);
      this.scroller.addEventListener('keydown', this.keydownHandler);
      window.addEventListener('resize', this.scheduleThumbHandler, { passive: true });
      // Measured in a frame rather than here: is-scrollable has to be on early
      // for the grab cursor, but AAS-5291 moved this read off parse so it stops
      // being the forced reflow Lighthouse pinned on this document.
      this.scheduleThumb();
    }

    destroy() {
      if (!this.scroller) return;

      this.scroller.removeEventListener('scroll', this.scheduleThumbHandler);
      this.scroller.removeEventListener('pointerdown', this.pointerDownHandler);
      this.scroller.removeEventListener('dragstart', this.dragStartHandler);
      this.scroller.removeEventListener('click', this.clickHandler, true);
      this.scroller.removeEventListener('keydown', this.keydownHandler);
      window.removeEventListener('resize', this.scheduleThumbHandler);

      if (this.thumbRaf) {
        cancelAnimationFrame(this.thumbRaf);
        this.thumbRaf = 0;
      }

      this.stopPointerTracking();
      this.scroller.classList.remove('is-dragging', 'is-scrollable');
    }

    setTabActive(tab, isActive) {
      tab.classList.toggle('is-active', isActive);

      if (isActive) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }

      const count = tab.querySelector('[data-tab-count]');
      if (count) count.hidden = !isActive;
    }

    syncActiveTab() {
      // main-collection-banner.liquid marks the active tab as it renders, so
      // there is nothing to repaint here in the normal case — matching on the
      // URL is only a fallback for markup that named no active tab.
      let active = this.tabs.find((tab) => tab.classList.contains('is-active'));

      if (!active) {
        active = this.matchTabToLocation();
        this.tabs.forEach((tab) => this.setTabActive(tab, tab === active));
      }

      this.scrollTabIntoView(active);
    }

    matchTabToLocation() {
      const path = stripTrailingSlash(window.location.pathname);

      // "All <X>" is rendered first, so it wins on an L1/L2 landing (where it
      // points at the current page); on an L3 page "All" points at the parent
      // and doesn't match, so the current child tab wins instead.
      return (
        this.tabs.find((tab) => stripTrailingSlash(tab.getAttribute('data-tab-url')) === path) ||
        this.tabs[0]
      );
    }

    scrollTabIntoView(tab) {
      if (!this.scroller || !tab) return;

      const tabRect = tab.getBoundingClientRect();
      const scrollerRect = this.scroller.getBoundingClientRect();
      const overflowRight = tabRect.right - scrollerRect.right;
      const overflowLeft = scrollerRect.left - tabRect.left;

      if (overflowRight > 0) {
        this.scroller.scrollLeft += overflowRight;
      } else if (overflowLeft > 0) {
        this.scroller.scrollLeft -= overflowLeft;
      }
    }

    updateThumb() {
      if (!this.scroller) return;

      const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
      const overflow = scrollWidth > clientWidth;

      this.scroller.classList.toggle('is-scrollable', overflow);

      if (!this.thumb || !this.track || !this.inner) return;

      if (!overflow) {
        this.track.style.display = 'none';
        return;
      }

      this.track.style.display = '';

      // AAS-5291: derive the thumb width from the CSS min-width, read once,
      // instead of writing a width and reading offsetWidth back. That
      // read-after-write forced a synchronous layout on every scroll frame.
      if (!this.minThumbReady) {
        this.minThumb = parseFloat(getComputedStyle(this.thumb).minWidth) || 40;
        this.minThumbReady = true;
      }

      const trackW = this.inner.clientWidth;
      const scrollable = scrollWidth - clientWidth;
      const thumbW = Math.max(this.minThumb, (clientWidth / scrollWidth) * trackW);
      const maxLeft = Math.max(trackW - thumbW, 0);
      const scrollRatio = scrollable > 0 ? scrollLeft / scrollable : 0;

      this.thumb.style.width = `${thumbW}px`;
      this.thumb.style.transform = `translateX(${scrollRatio * maxLeft}px)`;
    }

    scheduleThumb() {
      if (this.thumbRaf) return;

      this.thumbRaf = requestAnimationFrame(() => {
        this.thumbRaf = 0;
        this.updateThumb();
      });
    }

    handlePointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (!this.scroller || this.scroller.scrollWidth - this.scroller.clientWidth <= 1) return;

      this.dragStartX = event.clientX;
      this.dragStartScroll = this.scroller.scrollLeft;
      this.isDragging = false;
      this.suppressClick = false;

      window.addEventListener('pointermove', this.pointerMoveHandler, { passive: false });
      window.addEventListener('pointerup', this.pointerUpHandler);
      window.addEventListener('pointercancel', this.pointerUpHandler);
    }

    handlePointerMove(event) {
      const delta = event.clientX - this.dragStartX;

      if (!this.isDragging) {
        if (Math.abs(delta) <= DRAG_THRESHOLD) return;
        this.isDragging = true;
        this.suppressClick = true;
        this.scroller.classList.add('is-dragging');
        document.getSelection()?.removeAllRanges();
      }

      event.preventDefault();
      this.scroller.scrollLeft = this.dragStartScroll - delta;
    }

    handlePointerUp() {
      this.stopPointerTracking();
      this.scroller.classList.remove('is-dragging');
      this.isDragging = false;

      if (!this.suppressClick) return;

      // Click fires after pointerup; delay clearing so the capture handler can cancel it.
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }

    stopPointerTracking() {
      window.removeEventListener('pointermove', this.pointerMoveHandler, { passive: false });
      window.removeEventListener('pointerup', this.pointerUpHandler);
      window.removeEventListener('pointercancel', this.pointerUpHandler);
    }

    handleClick(event) {
      if (!this.suppressClick) return;

      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const currentIndex = this.tabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      event.preventDefault();

      const rtl = getComputedStyle(this.scroller).direction === 'rtl';
      let delta = event.key === 'ArrowRight' ? 1 : -1;
      if (rtl) delta = -delta;

      const next = this.tabs[currentIndex + delta];
      if (!next) return;

      next.focus();
      this.scrollTabIntoView(next);
    }
  }

  const instances = new WeakMap();

  const findRoot = (target) => {
    if (!target) return null;
    if (target.matches?.(ROOT_SELECTOR)) return target;
    return target.querySelector?.(ROOT_SELECTOR) ?? null;
  };

  const initRoot = (root) => {
    if (!root || instances.has(root)) return;
    instances.set(root, new CollectionBannerCategories(root));
  };

  const destroyRoot = (root) => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = findRoot(event.target);
    if (root) initRoot(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = findRoot(event.target);
    if (root) destroyRoot(root);
  });
})();
